/**
 * ACT004 热点问答条件（QUIZ）— 与小程序 xf_crm_mini 一致
 */

import { parseImageOptionLayoutConfig } from './quiz-image-option-layout'

export function normalizeQuizConditionType(raw) {
  const t = String(raw || '').toUpperCase()
  if (t === 'TIME') return 'TIME'
  if (t === 'QUIZ') return 'QUIZ'
  if (t === 'CHECKIN_COUNT') return 'CHECKIN_COUNT'
  return 'PREREQUISITE'
}

export function isTruthy(raw) {
  if (raw === true || raw === 1 || raw === '1') return true
  if (raw === false || raw === 0 || raw === '0' || raw == null) return false
  return String(raw).toLowerCase() === 'true'
}

export function isQuizHotspot(hotspot) {
  const cond = hotspot && hotspot.verifyCondition
  if (!cond || !isTruthy(cond.needValidate)) return false
  return normalizeQuizConditionType(cond.conditionType) === 'QUIZ'
}

export function getQuizFromLimitCache(act004LimitDetailByPoint, pointIndex) {
  const n = parseInt(pointIndex, 10)
  if (!Number.isFinite(n) || n <= 0) return null
  const raw = act004LimitDetailByPoint && act004LimitDetailByPoint[n]
  if (!raw || typeof raw !== 'object') return null
  return raw.quiz && typeof raw.quiz === 'object' ? raw.quiz : null
}

export function isRandomQuestionOrder(quiz) {
  const rules = quiz && quiz.paperRules && typeof quiz.paperRules === 'object' ? quiz.paperRules : {}
  return String(rules.questionOrderMode || '').toUpperCase() === 'RANDOM'
}

/**
 * batch / 单点限次接口 quiz 预取：用户是否已通过问答。
 * 注意：needQuiz 表示「热点配置了问答条件」，通过后仍为 true；是否跳过答题只看 quizPassed。
 */
export function isQuizPassedFromPrefetch(quiz) {
  if (!quiz || typeof quiz !== 'object') return false
  return isTruthy(quiz.quizPassed)
}

export function isQuizPassedForPoint(act004LimitDetailByPoint, pointIndex) {
  return isQuizPassedFromPrefetch(getQuizFromLimitCache(act004LimitDetailByPoint, pointIndex))
}

export function getQuizDisplayMode(quiz) {
  const rules = quiz && quiz.paperRules && typeof quiz.paperRules === 'object' ? quiz.paperRules : {}
  const mode = String(rules.displayMode || quiz.displayMode || 'DIALOG').toUpperCase()
  if (mode === 'PAGE_HOTSPOT') return 'PAGE_HOTSPOT'
  if (mode === 'IMAGE_OPTION_HOTSPOT') return 'IMAGE_OPTION_HOTSPOT'
  return 'DIALOG'
}

/** 背景图选项热点：SELECT_THEN_SUBMIT | CLICK_TO_SUBMIT（仅存于 layoutConfigJson） */
export function getQuizImageOptionSubmitMode(quiz) {
  const qs = quiz && Array.isArray(quiz.questions) ? quiz.questions : []
  for (let i = 0; i < qs.length; i++) {
    const layout = parseImageOptionLayoutConfig(qs[i] && qs[i].layoutConfigJson)
    const mode = layout && layout.imageOptionSubmitMode
      ? String(layout.imageOptionSubmitMode).trim().toUpperCase()
      : ''
    if (mode === 'CLICK_TO_SUBMIT') return 'CLICK_TO_SUBMIT'
    if (mode === 'SELECT_THEN_SUBMIT') return 'SELECT_THEN_SUBMIT'
  }
  return 'SELECT_THEN_SUBMIT'
}

export { resolveQuizStudyGate, resolveQuizStudyGateFromSources } from './quiz-study-layout'

export function buildQuizDialogPayload(quiz) {
  if (!quiz || !isTruthy(quiz.needQuiz)) return null
  return {
    quizPaperId: quiz.quizPaperId,
    quizPaperCode: quiz.quizPaperCode || '',
    paperTitle: quiz.paperTitle || '问答',
    paperRules: quiz.paperRules || {},
    questions: Array.isArray(quiz.questions) ? quiz.questions : [],
    questionsFromSession: isRandomQuestionOrder(quiz)
  }
}

/**
 * @returns {{ open: boolean, passed?: boolean, payload?: object, pointIndex?: number, reason?: string, displayMode?: string }}
 */
export function shouldOpenQuizDialog(hotspot, act004LimitDetailByPoint) {
  if (!isQuizHotspot(hotspot)) {
    return { open: false, reason: '' }
  }
  const pointIndex = parseInt(hotspot && hotspot.index, 10)
  const vc = hotspot.verifyCondition || {}
  const quiz = getQuizFromLimitCache(act004LimitDetailByPoint, pointIndex)

  if (!quiz) {
    if (vc.quizPaperId != null && String(vc.quizPaperId).trim() !== '') {
      return { open: false, reason: '问答数据未加载，请刷新页面或重新进入活动' }
    }
    return { open: false, reason: '问答数据未加载，请重新进入活动' }
  }

  if (!isTruthy(quiz.needQuiz)) {
    return {
      open: false,
      reason: '活动未同步问答配置，请在后台保存活动后重新进入'
    }
  }

  // 与小程序一致：needQuiz 不变，已通过则 open=false + passed=true
  if (isQuizPassedFromPrefetch(quiz)) {
    return { open: false, passed: true }
  }

  const payload = buildQuizDialogPayload(quiz)
  if (!payload) {
    return { open: false, reason: '问答题目未配置，请在后台完善问答卷' }
  }
  const hasQuestions = payload.questions && payload.questions.length > 0
  const sessionLoad = payload.questionsFromSession && payload.quizPaperId
  if (!hasQuestions && !sessionLoad) {
    return { open: false, reason: '问答题目未配置，请在后台完善问答卷' }
  }
  return {
    open: true,
    payload,
    pointIndex,
    displayMode: getQuizDisplayMode(quiz)
  }
}
